// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import {
  getClerkDomain,
  getClerkJsUrl,
  getClerkProxyUrl,
  getClerkPublishableKey,
} from '@/lib/authProvider';
import { ClerkProvider } from '@clerk/clerk-react';
import { Component, ComponentType, ReactNode } from 'react';
import { AuthSyncProvider } from './AuthSyncProvider';

// Clerk types its props as a union of mutually exclusive option sets (routing,
// satellite domains, ...). The options here are built from configuration, so
// the component is typed by what this file actually passes it.
const Provider = ClerkProvider as unknown as ComponentType<
  Record<string, unknown> & { children: ReactNode }
>;

interface BoundaryState {
  error: Error | null;
}

/** A broken Clerk SDK (blocked script, bad key) must not leave a blank page. */
class ClerkErrorBoundary extends Component<
  { children: ReactNode },
  BoundaryState
> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('[ClerkErrorBoundary] Clerk SDK error:', error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h2 className="text-lg font-semibold text-foreground">
          Authentication service temporarily unavailable
        </h2>
        <p className="text-sm text-muted-foreground">
          {this.state.error.message || 'Please try refreshing the page.'}
        </p>
        <button
          className="rounded-md border border-border px-4 py-2 text-sm"
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    );
  }
}

/** Mounts Clerk and the token sync around the app. Only used when a key is configured. */
export function ClerkProviders({ children }: { children: ReactNode }) {
  // Only the settings that are configured.
  const clerkProps: Record<string, unknown> = {
    publishableKey: getClerkPublishableKey() ?? '',
  };
  const domain = getClerkDomain();
  const proxyUrl = getClerkProxyUrl();
  const clerkJSUrl = getClerkJsUrl();
  if (domain) clerkProps.domain = domain;
  if (proxyUrl) clerkProps.proxyUrl = proxyUrl;
  if (clerkJSUrl) clerkProps.clerkJSUrl = clerkJSUrl;

  return (
    <ClerkErrorBoundary>
      <Provider {...clerkProps}>
        <AuthSyncProvider>{children}</AuthSyncProvider>
      </Provider>
    </ClerkErrorBoundary>
  );
}
